/**
 * SuggestionCard — displays a single coding suggestion.
 *
 * Visual hierarchy:
 *   [Gap type badge] [ICD code] [Description]     [HCC weight] [Accept/Reject]
 *   [Last captured date]
 *   [Expandable evidence panel]
 *
 * Suspect codes (gapType === 'suspect_new') get blue left border — primary feature.
 * Recaptured Existing = purple, Suspecting Emerging = amber.
 */
import clsx from 'clsx';
import type { CodingSuggestion } from '../types';
import { AcceptButton } from './AcceptButton';
import { EvidencePanel } from './EvidencePanel';

interface Props {
  suggestion: CodingSuggestion;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  onEdit?: () => void;
  onUndo?: () => void;
  isLoading?: boolean;
}

// ─── Gap type metadata ────────────────────────────────────────────────────────

const GAP_META = {
  suspect_new: {
    label: 'Suspecting Code Gap',
    shortLabel: 'Suspecting Code Gap',
    borderColor: 'border-l-suspect',
    badgeBg: 'bg-suspect-badge text-suspect border border-suspect-border',
    cardBg: 'bg-white hover:bg-suspect-bg/30',
  },
  opportunity: {
    label: 'Recaptured Opportunity',
    shortLabel: 'Recaptured Opportunity',
    borderColor: 'border-l-opportunity',
    badgeBg: 'bg-opportunity-badge text-opportunity border border-opportunity-border',
    cardBg: 'bg-white hover:bg-opportunity-bg/30',
  },
  recapture: {
    label: 'Recaptured Existing',
    shortLabel: 'Recaptured Existing',
    borderColor: 'border-l-recapture',
    badgeBg: 'bg-recapture-badge text-recapture border border-recapture-border',
    cardBg: 'bg-white hover:bg-recapture-bg/30',
  },
  recapture_stale: {
    label: 'Suspecting Emerging',
    shortLabel: 'Suspecting Emerging',
    borderColor: 'border-l-stale',
    badgeBg: 'bg-stale-badge text-stale border border-stale-border',
    cardBg: 'bg-white hover:bg-stale-bg/30',
  },
  over_claimed: {
    label: 'Recaptured Risk',
    shortLabel: 'Recaptured Risk',
    borderColor: 'border-l-overclaimed',
    badgeBg: 'bg-overclaimed-badge text-overclaimed border border-overclaimed-border',
    cardBg: 'bg-white hover:bg-overclaimed-bg/30',
  },
} as const;


// ─── Component ────────────────────────────────────────────────────────────────

export function SuggestionCard({
  suggestion,
  isExpanded,
  onToggleExpand,
  onAccept,
  onReject,
  onEdit,
  onUndo,
  isLoading = false,
}: Props) {
  const gapMeta = GAP_META[suggestion.gapType];
  const isActioned = suggestion.status !== 'pending';
  const isNonHCC = !suggestion.hccCategory;

  const cardBg = suggestion.status === 'accepted'
    ? 'bg-accepted-bg'
    : suggestion.status === 'rejected'
    ? 'bg-rejected-bg opacity-60'
    : isNonHCC && suggestion.status === 'pending'
    ? 'bg-gray-50/80 hover:bg-gray-50'
    : gapMeta.cardBg;

  return (
    <div
      className={clsx(
        'border border-gray-200 rounded-lg border-l-4 overflow-hidden transition-all duration-200',
        gapMeta.borderColor,
        cardBg,
        isActioned && 'opacity-90',
        isNonHCC && suggestion.status === 'pending' && 'opacity-75',
      )}
    >
      {/* ── Main row ─────────────────────────────────────────────────────── */}
      <div
        className="px-4 py-3 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if ((e.target as HTMLElement).closest('button, [role="button"], input, select, textarea')) return;
          e.preventDefault();
          onToggleExpand();
        }}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} evidence for ${suggestion.icdCode} — ${suggestion.icdDescription}`}
      >
        {/* Row 1: badges (left) + action (right) */}
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0',
                gapMeta.badgeBg,
              )}
            >
              {gapMeta.shortLabel}
            </span>
            {suggestion.hccCategory && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200 flex-shrink-0">
                {suggestion.hccCategory}
              </span>
            )}
          </div>
          <div
            className="flex items-center gap-3 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <AcceptButton
              status={suggestion.status}
              gapType={suggestion.gapType}
              isLoading={isLoading}
              onAccept={onAccept}
              onReject={onReject}
              onEdit={onEdit}
              onUndo={onUndo}
            />
          </div>
        </div>

        {/* Row 2: ICD code + description — primary focal point */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono font-bold text-lg text-gray-900 tracking-wide">
            {suggestion.icdCode}
          </span>
          <span className="text-sm font-semibold text-gray-800 leading-snug">
            {suggestion.icdDescription}
          </span>
        </div>

        {/* Justification */}
        {suggestion.justification && (
          <p className="mt-1 text-xs text-gray-600 leading-relaxed">
            {suggestion.justification}
          </p>
        )}

        {/* Row 2: dates + chevron */}
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Last captured date */}
            {suggestion.lastCapturedDate && (
              <span className="text-xs text-gray-500">
                Last captured:{' '}
                {new Date(suggestion.lastCapturedDate + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}

            {suggestion.gapType === 'suspect_new' && (
              <span className="text-xs text-blue-600 font-medium">Never captured</span>
            )}

            {suggestion.gapType === 'opportunity' && (
              <span className="text-xs text-green-700 font-medium">Coded — needs higher specificity</span>
            )}

            {suggestion.gapType === 'over_claimed' && (
              <span className="text-xs text-red-700 font-medium">Prior claim — evidence not found</span>
            )}

            {/* Write-back confirmation */}
            {suggestion.status === 'accepted' && suggestion.writeBack && (
              <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Written to EHR ({suggestion.writeBack.method})
              </span>
            )}
          </div>

          {/* Expand chevron + evidence count */}
          <div className="flex items-center gap-1.5 text-gray-400">
            <span className="text-xs">{suggestion.evidence.length} source{suggestion.evidence.length !== 1 ? 's' : ''}</span>
            <svg
              className={clsx('w-4 h-4 transition-transform duration-200', isExpanded && 'rotate-180')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Evidence panel ──────────────────────────────────────────────── */}
      {isExpanded && (
        <EvidencePanel
          evidence={suggestion.evidence}
          clinicalReasoning={suggestion.clinicalReasoning}
        />
      )}
    </div>
  );
}
